/**
 * BE#710 — shape returned by `POST /actions {cmd:'campaign-failure-breakdown', campaignId}`
 * (JWT, owner-checked). Counts are computed BE-side over the whole campaign (the recipient
 * list is lazy-loaded, so a client-side count over loaded rows would undercount).
 */
export interface CampaignFailureBreakdown {
  campaignId: string;
  total: number;
  failedTotal: number;
  /** failure reason code → count (e.g. { not_on_whatsapp: 12, rate_limited: 1 }). */
  byReason: Record<string, number>;
  notOnWhatsAppCount: number;
  /** BE#613 — groups WhatsApp itself rejected (ack error) in this campaign. Absent on older backends. */
  rejectedGroups?: RejectedGroup[];
}

export interface RejectedGroup {
  chatId: string;
  name: string;
  count: number;
  /** The owner's previous send to this group also failed — a repeat, not a one-off. */
  chronic: boolean;
}

/** The report row lists chronic groups only (a one-off rejection is noise), biggest first. */
export function chronicRejectedGroups(
  breakdown: Pick<CampaignFailureBreakdown, 'rejectedGroups'> | null | undefined,
): RejectedGroup[] {
  return (breakdown?.rejectedGroups ?? [])
    .filter((g) => g.chronic)
    .sort((a, b) => b.count - a.count || (a.name ?? '').localeCompare(b.name ?? ''));
}

/** A banner needs at least this many not-on-WhatsApp failures to be worth showing
 *  (a single stray invalid number is covered by the per-row label alone). */
export const NOT_ON_WHATSAPP_BANNER_MIN_COUNT = 3;
/** …and they must be at least this share of all terminal failures. */
export const NOT_ON_WHATSAPP_BANNER_MIN_RATIO = 0.2;

/**
 * Should the campaign-level "numbers aren't on WhatsApp" banner show?
 *
 * Trigger (spec 4c): `notOnWhatsAppCount >= 3` AND it is at least 20% of all failures —
 * OR every failure is this reason (and still >= 3). The "all this reason" clause is a
 * belt-and-braces restatement: when every failure is this reason the ratio is 1.0 and
 * already clears the threshold, but it is kept explicit so the intent survives a future
 * threshold edit.
 */
export function shouldShowNotOnWhatsAppBanner(
  breakdown: Pick<CampaignFailureBreakdown, 'failedTotal' | 'notOnWhatsAppCount'> | null | undefined,
): boolean {
  if (!breakdown) return false;
  const { notOnWhatsAppCount, failedTotal } = breakdown;
  if (!Number.isFinite(notOnWhatsAppCount) || notOnWhatsAppCount < NOT_ON_WHATSAPP_BANNER_MIN_COUNT) return false;
  if (!Number.isFinite(failedTotal) || failedTotal <= 0) return false;

  const allThisReason = notOnWhatsAppCount >= failedTotal;
  const ratio = notOnWhatsAppCount / failedTotal;
  return allThisReason || ratio >= NOT_ON_WHATSAPP_BANNER_MIN_RATIO;
}
